import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiFetch, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import MainNav from '../components/MainNav';
import { ShareButtons } from '../components/ShareButtons';
import { 
  Music, Play, Pause, Download, Share2, Search, 
  Cake, Loader2, Volume2, VolumeX, RotateCcw, ChevronLeft,
  Video, ImagePlus, Upload, X, Sparkles, Maximize, Check, Gift
} from 'lucide-react';

// HP server base URL for birthday songs (migrated from Cloudinary Apr 22 2026)
const BIRTHDAY_SONG_BASE = 'https://media.wrappedinmusic.com/audio/birthday_songs';

// Default song URL (for names not in the personalized list)
const DEFAULT_BIRTHDAY_SONG = 'https://media.wrappedinmusic.com/audio/da/hb_default.mp3';

// Available names (70 names - alphabetically sorted)
const AVAILABLE_NAMES = [
  'amber', 'barbara', 'betty', 'billy', 'brandon', 'brian',
  'carol', 'charles', 'charlie',
  'daniel', 'david', 'deborah', 'donald', 'donna', 'donny', 'dorothy',
  'edward', 'elizabeth',
  'finley',
  'george',
  'helen',
  'jack', 'jake', 'james', 'janet', 'jason', 'jeff', 'jennifer', 'jill', 
  'john', 'johnny', 'joseph',
  'karen', 'katrina', 'kenneth', 'kevin', 'kimberly',
  'lance', 'laura', 'linda', 'lisa',
  'margaret', 'maria', 'marjorie', 'mark', 'mary', 'maverick', 'michael', 'michelle',
  'nancy',
  'patricia', 'paul',
  'rena', 'richard', 'robert', 'ronald', 'rory', 'ruth', 'ryker',
  'samuel', 'sandra', 'sarah', 'scott', 'sharon', 'shawna', 'steven', 'susan',
  'thomas',
  'wayne', 'william'
];

export default function BirthdaySong() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Audio source selection (first choice)
  const [audioSource, setAudioSource] = useState('birthday'); // 'birthday' or 'custom'
  
  // Name input with autocomplete
  const [nameInput, setNameInput] = useState('');
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [filteredNames, setFilteredNames] = useState([]);
  const [isPersonalizedName, setIsPersonalizedName] = useState(false);
  
  // Selected values
  const [selectedName, setSelectedName] = useState(null); // The final name for video text
  const [recipientName, setRecipientName] = useState(''); // For custom song - recipient name input
  
  // Audio/playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioReady, setAudioReady] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [cost, setCost] = useState(0); // 0 = free
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  // Video generation state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoStyle, setVideoStyle] = useState('balloons');
  const [videoOverlay, setVideoOverlay] = useState('none');
  const [videoOrientation, setVideoOrientation] = useState('square');
  const [textPosition, setTextPosition] = useState('bottom');
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [customImageFile, setCustomImageFile] = useState(null);
  const [customVideoUrl, setCustomVideoUrl] = useState('');
  const [customVideoFile, setCustomVideoFile] = useState(null);
  const [availableBackgrounds, setAvailableBackgrounds] = useState([]);
  const [availableOverlays, setAvailableOverlays] = useState([]);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
  const [generatedVideoId, setGeneratedVideoId] = useState(null);
  const [videoPollingId, setVideoPollingId] = useState(null);
  
  // User's songs (for custom audio option)
  const [userSongs, setUserSongs] = useState([]);
  const [selectedUserSong, setSelectedUserSong] = useState(null);
  const [loadingUserSongs, setLoadingUserSongs] = useState(false);
  
  // Audio refs
  const audioContextRef = useRef(null);
  const baseBufferRef = useRef(null);
  const nameBufferRef = useRef(null);
  const sourceRef = useRef(null);
  const gainNodeRef = useRef(null);
  const startTimeRef = useRef(0);
  const pauseTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const nameInputRef = useRef(null);
  
  // Configuration
  const NAME_INSERT_TIME = 10; // seconds
  
  // Filter names based on input with autocomplete
  useEffect(() => {
    if (nameInput.length > 0) {
      const query = nameInput.toLowerCase();
      const matches = AVAILABLE_NAMES.filter(name => 
        name.toLowerCase().startsWith(query) || 
        name.toLowerCase().includes(query)
      ).sort((a, b) => {
        // Prioritize names that start with the query
        const aStarts = a.toLowerCase().startsWith(query);
        const bStarts = b.toLowerCase().startsWith(query);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b);
      });
      setFilteredNames(matches);
      setShowNameSuggestions(matches.length > 0);
      
      // Check if it's a personalized name
      const exactMatch = AVAILABLE_NAMES.find(n => n.toLowerCase() === query);
      setIsPersonalizedName(!!exactMatch);
    } else {
      setFilteredNames([]);
      setShowNameSuggestions(false);
      setIsPersonalizedName(false);
    }
  }, [nameInput]);
  
  // Handle name selection from autocomplete
  const handleSelectName = (name) => {
    const capitalized = name.charAt(0).toUpperCase() + name.slice(1);
    setNameInput(capitalized);
    setSelectedName(name.toLowerCase());
    setShowNameSuggestions(false);
    setIsPersonalizedName(true);
  };
  
  // Handle custom name entry (not in list)
  const handleCustomName = () => {
    if (nameInput.trim()) {
      setSelectedName(nameInput.trim().toLowerCase());
      setShowNameSuggestions(false);
      setIsPersonalizedName(false);
    }
  };
  
  // Fetch cost setting
  useEffect(() => {
    const fetchCost = async () => {
      try {
        const { data } = await apiFetch('/credits/costs');
        if (data?.birthday_song_cost !== undefined) {
          setCost(data.birthday_song_cost);
        }
      } catch (error) {
        console.error('Error fetching costs:', error);
      }
    };
    fetchCost();
  }, []);
  
  // Handle URL parameter for deep linking (e.g., /birthday?name=brian)
  useEffect(() => {
    if (initialLoadDone) return;
    
    const nameParam = searchParams.get('name');
    if (nameParam) {
      const normalizedName = nameParam.toLowerCase();
      if (AVAILABLE_NAMES.includes(normalizedName)) {
        setSelectedName(normalizedName);
        setInitialLoadDone(true);
      }
    }
  }, [searchParams, initialLoadDone]);
  
  // Fetch available backgrounds
  useEffect(() => {
    const fetchBackgrounds = async () => {
      try {
        const { data } = await apiFetch('/birthday/backgrounds');
        if (data?.backgrounds) {
          setAvailableBackgrounds(data.backgrounds);
        }
      } catch (error) {
        console.error('Error fetching backgrounds:', error);
      }
    };
    fetchBackgrounds();
  }, []);
  
  // Fetch available overlays
  useEffect(() => {
    const fetchOverlays = async () => {
      try {
        const { data } = await apiFetch('/birthday/overlays');
        if (data?.overlays) {
          setAvailableOverlays(data.overlays);
        }
      } catch (error) {
        console.error('Error fetching overlays:', error);
      }
    };
    fetchOverlays();
  }, []);
  
  // Fetch user's songs when custom audio source is selected
  useEffect(() => {
    const fetchUserSongs = async () => {
      if (audioSource !== 'custom' || !user) return;
      
      setLoadingUserSongs(true);
      try {
        const { data } = await apiFetch('/songs');
        console.log('BirthdaySong: Raw songs data:', data?.length || 0);
        // API returns array directly - same as CreateGift and CreateCover pages
        const songsArray = Array.isArray(data) ? data : [];
        // Filter to completed songs with audio - matching CreateGift/CreateCover behavior
        const availableSongs = songsArray.filter(s => s.status === 'completed' && s.audio_url);
        console.log('BirthdaySong: Filtered songs:', availableSongs.length);
        setUserSongs(availableSongs);
      } catch (error) {
        console.error('Error fetching user songs:', error);
        setUserSongs([]);
      } finally {
        setLoadingUserSongs(false);
      }
    };
    fetchUserSongs();
  }, [audioSource, user]);
  
  // Load audio buffers when name is selected
  const loadAudio = useCallback(async (name, useDefaultSong = false) => {
    setIsLoading(true);
    setAudioReady(false);
    
    try {
      // Create audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      // Check if we have a personalized name clip
      const normalizedName = name.toLowerCase();
      const hasPersonalizedName = AVAILABLE_NAMES.includes(normalizedName);
      
      if (hasPersonalizedName && !useDefaultSong) {
        // Load base track and name clip in parallel
        const [baseResponse, nameResponse] = await Promise.all([
          fetch(`${BIRTHDAY_SONG_BASE}/hb_base.mp3`),
          fetch(`${BIRTHDAY_SONG_BASE}/hb_name_${normalizedName}.mp3`)
        ]);
        
        if (!baseResponse.ok || !nameResponse.ok) {
          throw new Error('Failed to load audio files');
        }
        
        const [baseData, nameData] = await Promise.all([
          baseResponse.arrayBuffer(),
          nameResponse.arrayBuffer()
        ]);
        
        // Decode audio data
        const [baseBuffer, nameBuffer] = await Promise.all([
          ctx.decodeAudioData(baseData),
          ctx.decodeAudioData(nameData)
        ]);
        
        baseBufferRef.current = baseBuffer;
        nameBufferRef.current = nameBuffer;
        setDuration(baseBuffer.duration);
        setAudioReady(true);
        
        toast.success(`Ready to play personalized birthday song for ${name}!`);
      } else {
        // Use the default "Happy Happy Birthday" song (no name)
        const response = await fetch(DEFAULT_BIRTHDAY_SONG);
        
        if (!response.ok) {
          throw new Error('Failed to load default birthday song');
        }
        
        const audioData = await response.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(audioData);
        
        // For default song, set base buffer and create empty name buffer
        baseBufferRef.current = audioBuffer;
        nameBufferRef.current = null; // No name clip for default song
        setDuration(audioBuffer.duration);
        setAudioReady(true);
        
        toast.success(`Ready to play birthday song for ${name}!`);
      }
    } catch (error) {
      console.error('Error loading audio:', error);
      toast.error('Failed to load audio files');
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Load audio when selectedName is set (for birthday songs)
  useEffect(() => {
    if (selectedName && audioSource === 'birthday') {
      const useDefault = !AVAILABLE_NAMES.includes(selectedName.toLowerCase());
      loadAudio(selectedName, useDefault);
    }
  }, [selectedName, audioSource, loadAudio]);

  // Load custom song audio when selectedName is set with a custom song
  useEffect(() => {
    const loadCustomSongAudio = async () => {
      if (!selectedName || audioSource !== 'custom' || !selectedUserSong?.audio_url) return;
      
      setIsLoading(true);
      setAudioReady(false);
      
      try {
        // Create audio context if needed
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        const ctx = audioContextRef.current;
        
        // Resume context if suspended (browser autoplay policy)
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        // Load the custom song
        const response = await fetch(selectedUserSong.audio_url);
        if (!response.ok) {
          throw new Error('Failed to load custom song');
        }
        
        const audioData = await response.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(audioData);
        
        // Store in base buffer (we'll use same playback logic)
        baseBufferRef.current = audioBuffer;
        nameBufferRef.current = null; // No name clip for custom songs
        setDuration(audioBuffer.duration);
        setAudioReady(true);
        
        toast.success(`Ready to play: ${selectedUserSong.title}`);
      } catch (error) {
        console.error('Error loading custom song:', error);
        toast.error('Failed to load song. Please try again.');
        setAudioReady(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadCustomSongAudio();
  }, [selectedName, audioSource, selectedUserSong]);
  
  // Stop playback
  const stopPlayback = useCallback(() => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch (e) {
        // Already stopped
      }
      sourceRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setIsPlaying(false);
    setProgress(0);
    pauseTimeRef.current = 0;
  }, []);
  
  // Reset to initial state (go back to audio selection)
  const resetSelection = useCallback(() => {
    stopPlayback();
    setSelectedName(null);
    setSelectedUserSong(null);
    setNameInput('');
    setRecipientName('');
    setAudioReady(false);
    setIsPersonalizedName(false);
  }, [stopPlayback]);
  
  // Play mixed audio
  const playMixedAudio = useCallback((startOffset = 0) => {
    if (!audioContextRef.current || !baseBufferRef.current) {
      return;
    }
    
    const ctx = audioContextRef.current;
    
    // Create gain node for volume control
    if (!gainNodeRef.current) {
      gainNodeRef.current = ctx.createGain();
      gainNodeRef.current.connect(ctx.destination);
    }
    gainNodeRef.current.gain.value = isMuted ? 0 : 1;
    
    // Create and configure base track source
    const baseSource = ctx.createBufferSource();
    baseSource.buffer = baseBufferRef.current;
    baseSource.connect(gainNodeRef.current);
    
    // Store reference for stopping
    sourceRef.current = baseSource;
    
    // Calculate timing
    const now = ctx.currentTime;
    startTimeRef.current = now - startOffset;
    
    // Start base track
    baseSource.start(now, startOffset);
    
    // Only add name clip if we have a personalized name buffer
    if (nameBufferRef.current) {
      // Create and configure name clip source
      const nameSource = ctx.createBufferSource();
      nameSource.buffer = nameBufferRef.current;
      nameSource.connect(gainNodeRef.current);
      
      // Start name clip at the insertion point (if we haven't passed it)
      const nameStartTime = NAME_INSERT_TIME - startOffset;
      if (nameStartTime > 0) {
        nameSource.start(now + nameStartTime);
      } else if (startOffset < NAME_INSERT_TIME + nameBufferRef.current.duration) {
        // We're in the middle of the name - start from offset
        const nameOffset = startOffset - NAME_INSERT_TIME;
        nameSource.start(now, nameOffset);
      }
    }
    
    // Handle playback end
    baseSource.onended = () => {
      setIsPlaying(false);
      setProgress(0);
      pauseTimeRef.current = 0;
    };
    
    setIsPlaying(true);
    
    // Update progress
    const updateProgress = () => {
      if (audioContextRef.current && isPlaying) {
        const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
        const prog = Math.min(elapsed / duration, 1);
        setProgress(prog);
        
        if (prog < 1) {
          animationFrameRef.current = requestAnimationFrame(updateProgress);
        }
      }
    };
    animationFrameRef.current = requestAnimationFrame(updateProgress);
  }, [isMuted, duration, isPlaying]);
  
  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (!audioReady) return;
    
    if (isPlaying) {
      // Pause
      if (sourceRef.current) {
        pauseTimeRef.current = audioContextRef.current.currentTime - startTimeRef.current;
        sourceRef.current.stop();
        sourceRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setIsPlaying(false);
    } else {
      // Play from pause position
      playMixedAudio(pauseTimeRef.current);
    }
  }, [audioReady, isPlaying, playMixedAudio]);
  
  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      if (gainNodeRef.current) {
        gainNodeRef.current.gain.value = newMuted ? 0 : 1;
      }
      return newMuted;
    });
  }, []);
  
  // Restart playback
  const restartPlayback = useCallback(() => {
    stopPlayback();
    setTimeout(() => {
      playMixedAudio(0);
    }, 100);
  }, [stopPlayback, playMixedAudio]);
  
  // Download the full song
  const downloadAudio = useCallback(async () => {
    if (!selectedName) return;
    
    // Download the pre-mixed full song from HP server
    const url = `${BIRTHDAY_SONG_BASE}/hb_full_${selectedName.toLowerCase()}.mp3`;
    
    // Fetch and download as blob to ensure it downloads
    try {
      toast.info('Preparing download...');
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `happy_birthday_${selectedName.toLowerCase()}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download');
    }
  }, [selectedName]);
  
  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle custom image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    
    setCustomImageFile(file);
    setVideoStyle('custom_image');
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setCustomImageUrl(previewUrl);
  };
  
  // Handle custom video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    
    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }
    
    setCustomVideoFile(file);
    setVideoStyle('custom_video');
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setCustomVideoUrl(previewUrl);
  };
  
  // Upload custom image to HP server via backend
  const uploadCustomImage = async () => {
    if (!customImageFile) return null;
    
    const formData = new FormData();
    formData.append('file', customImageFile);
    
    try {
      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Image upload error:', error);
      return null;
    }
  };
  
  // Upload custom video to HP server via backend
  const uploadCustomVideo = async () => {
    if (!customVideoFile) return null;
    
    const formData = new FormData();
    formData.append('file', customVideoFile);
    
    try {
      const response = await fetch(`${API}/upload/video`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Video upload error:', error);
      return null;
    }
  };
  
  // Poll for video status
  const pollVideoStatus = useCallback(async (videoId) => {
    try {
      const { data } = await apiFetch(`/videos/${videoId}`);
      
      if (data.status === 'completed') {
        setIsGeneratingVideo(false);
        setGeneratedVideoUrl(data.video_url);
        setGeneratedVideoId(data.video_id);
        setVideoPollingId(null);
        toast.success('🎉 Video ready! Scroll down to view and download.');
        // Scroll to video section
        setTimeout(() => {
          const videoSection = document.getElementById('birthday-video-player');
          if (videoSection) {
            videoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
      } else if (data.status === 'failed') {
        setIsGeneratingVideo(false);
        setVideoPollingId(null);
        toast.error('Video generation failed');
      } else {
        // Still processing, poll again
        setTimeout(() => pollVideoStatus(videoId), 2000);
      }
    } catch (error) {
      console.error('Poll error:', error);
      setIsGeneratingVideo(false);
      setVideoPollingId(null);
    }
  }, []);
  
  // Generate birthday video
  const generateVideo = async () => {
    if (!selectedName || !user) {
      toast.error('Please log in to create videos');
      return;
    }
    
    setIsGeneratingVideo(true);
    setGeneratedVideoUrl(null);
    setGeneratedVideoId(null);
    
    try {
      // Upload custom media if selected
      let imageUrl = null;
      let videoUrl = null;
      
      if (videoStyle === 'custom_image' && customImageFile) {
        toast.info('Uploading image...');
        imageUrl = await uploadCustomImage();
        if (!imageUrl) {
          throw new Error('Failed to upload custom image');
        }
      } else if (videoStyle === 'custom_video' && customVideoFile) {
        toast.info('Uploading video... This may take a moment');
        videoUrl = await uploadCustomVideo();
        if (!videoUrl) {
          throw new Error('Failed to upload custom video');
        }
      }
      
      // Determine audio source
      const customAudioUrl = audioSource === 'custom' && selectedUserSong 
        ? selectedUserSong.audio_url 
        : null;
      
      const { data } = await apiFetch('/birthday/create-video', {
        method: 'POST',
        body: JSON.stringify({
          name: selectedName,
          style: videoStyle,
          custom_image_url: imageUrl,
          custom_video_url: videoUrl,
          custom_audio_url: customAudioUrl,  // Pass custom song audio
          overlay: videoOverlay,
          orientation: videoOrientation,
          text_animation: true,
          text_position: textPosition
        })
      });
      
      toast.info('Creating video... This may take a minute');
      setVideoPollingId(data.video_id);
      pollVideoStatus(data.video_id);
      
    } catch (error) {
      console.error('Video generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start video generation');
      setIsGeneratingVideo(false);
    }
  };
  
  // Download video
  // We always blob-fetch the video so the browser triggers a save dialog,
  // even when the URL is cross-origin (HP server). The `download` attribute
  // is only honored same-origin, so the previous fast-path with `target=_blank`
  // would open the video in a new tab instead of downloading it.
  const downloadVideo = async () => {
    if (!generatedVideoId && !generatedVideoUrl) return;

    let toastId;
    try {
      toastId = toast.loading('Preparing download...');

      // Prefer the backend proxy (same-origin, attaches Content-Disposition).
      // Fall back to the direct HP URL only if there's no video_id.
      const downloadUrl = generatedVideoId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/videos/${generatedVideoId}/download`
        : generatedVideoUrl;

      const response = await fetch(downloadUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const safeName = (selectedName || 'birthday').toString()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '_')
        .slice(0, 40);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `happy_birthday_${safeName}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Give the browser a moment to capture the blob before revoking
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

      toast.success('Download started!', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download video — please try again', { id: toastId });
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPlayback();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopPlayback]);
  
  return (
    <div className="min-h-screen bg-background">
      <MainNav />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-white/60 hover:text-white"
          data-testid="back-to-dashboard"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 mb-4">
            <Cake className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Birthday Video Creator
          </h1>
          <p className="text-white/60 max-w-md mx-auto">
            Create a personalized birthday video to share with someone special!
          </p>
        </div>
        
        {/* Step 1: Audio Source Selection - shown until name is confirmed */}
        {!selectedName ? (
          <div className="glass-card rounded-xl p-6 md:p-8" data-testid="audio-source-selection">
            <h2 className="text-xl font-semibold text-white mb-6 text-center">
              Choose Your Audio
            </h2>
            
            {/* Audio Source Toggle */}
            <div className="grid grid-cols-2 gap-4 mb-8 max-w-lg mx-auto">
              <button
                onClick={() => {
                  setAudioSource('birthday');
                  setSelectedUserSong(null);
                  setRecipientName('');
                }}
                className={`p-6 rounded-xl border-2 transition-all ${
                  audioSource === 'birthday'
                    ? 'border-purple-500 bg-purple-500/20'
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
              >
                <Cake className="w-10 h-10 mx-auto mb-3 text-pink-400" />
                <span className="text-white font-medium block">Our Birthday Song</span>
                <span className="text-white/40 text-sm">Personalized with their name</span>
                <span className="text-green-400 text-xs block mt-2">FREE</span>
              </button>
              
              <button
                onClick={() => {
                  setAudioSource('custom');
                  setNameInput('');
                  setSelectedName(null);
                }}
                disabled={!user}
                className={`p-6 rounded-xl border-2 transition-all ${
                  audioSource === 'custom'
                    ? 'border-purple-500 bg-purple-500/20'
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Music className="w-10 h-10 mx-auto mb-3 text-purple-400" />
                <span className="text-white font-medium block">My Song</span>
                <span className="text-white/40 text-sm">Use a song you created</span>
                <span className="text-amber-400 text-xs block mt-2">5 Credits</span>
              </button>
            </div>
            
            {/* Conditional Content based on Audio Source */}
            {audioSource === 'birthday' ? (
              /* Our Birthday Song - Name Input with Autocomplete */
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Who is the birthday for?
                </label>
                <div className="relative">
                  <Input
                    ref={nameInputRef}
                    type="text"
                    placeholder="Type a name (e.g., David, Sarah...)"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onFocus={() => nameInput && setShowNameSuggestions(filteredNames.length > 0)}
                    onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && nameInput.trim()) {
                        handleCustomName();
                      }
                    }}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 text-lg py-6"
                    data-testid="name-input"
                  />
                  
                  {/* Autocomplete Suggestions */}
                  {showNameSuggestions && filteredNames.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                      {filteredNames.slice(0, 8).map((name) => (
                        <button
                          key={name}
                          onClick={() => handleSelectName(name)}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 capitalize flex items-center gap-2"
                        >
                          <Check className="w-4 h-4 text-green-400" />
                          {name}
                          <span className="text-white/40 text-xs ml-auto">Personalized song</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Status indicator */}
                {nameInput && (
                  <div className="mt-3 text-sm">
                    {isPersonalizedName ? (
                      <p className="text-green-400 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        We have a personalized song for {nameInput}!
                      </p>
                    ) : (
                      <p className="text-amber-400">
                        We'll use our classic "Happy Happy Birthday" tune for {nameInput}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Continue Button */}
                <Button
                  onClick={handleCustomName}
                  disabled={!nameInput.trim()}
                  className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 py-6"
                  data-testid="continue-with-name"
                >
                  Continue with {nameInput || 'this name'}
                </Button>
                
                <p className="text-center text-white/40 text-sm mt-4">
                  {AVAILABLE_NAMES.length} personalized names available
                </p>
              </div>
            ) : (
              /* My Song - Song Library Selection */
              <div className="max-w-md mx-auto">
                {!user ? (
                  <div className="text-center py-8">
                    <p className="text-white/60 mb-4">Log in to use your own songs</p>
                    <Button onClick={() => navigate('/login')} className="btn-primary">
                      Log In
                    </Button>
                  </div>
                ) : loadingUserSongs ? (
                  <div className="flex items-center justify-center py-8 text-white/60">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Loading your songs...
                  </div>
                ) : userSongs.length === 0 ? (
                  <div className="text-center py-8 text-white/60">
                    <p className="mb-4">You don't have any completed songs yet.</p>
                    <Button onClick={() => navigate('/create')} className="btn-primary">
                      Create Your First Song
                    </Button>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Select a song from your library
                    </label>
                    <div className="max-h-64 overflow-y-auto space-y-2 mb-6 pr-2">
                      {userSongs.map((song) => (
                        <button
                          key={song.song_id}
                          onClick={() => setSelectedUserSong(song)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                            selectedUserSong?.song_id === song.song_id
                              ? 'border-purple-500 bg-purple-500/20'
                              : 'border-white/10 bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          {song.image_url ? (
                            <img
                              src={song.image_url}
                              alt={song.title}
                              className="w-12 h-12 rounded object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center">
                              <Music className="w-6 h-6 text-white/40" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{song.title}</p>
                            <p className="text-white/40 text-sm truncate">{song.genre || 'No genre'}</p>
                          </div>
                          {selectedUserSong?.song_id === song.song_id && (
                            <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                    
                    {/* Recipient Name Input */}
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Who is the birthday for?
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter recipient's name"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-4"
                      data-testid="recipient-name-input"
                    />
                    
                    {/* Continue Button */}
                    <Button
                      onClick={() => {
                        if (selectedUserSong && recipientName.trim()) {
                          setSelectedName(recipientName.trim().toLowerCase());
                        }
                      }}
                      disabled={!selectedUserSong || !recipientName.trim()}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      data-testid="continue-with-song"
                    >
                      Continue (5 Credits)
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Player */
          <div className="glass-card rounded-xl p-6 md:p-8" data-testid="birthday-player">
            {/* Selected Name Display */}
            <div className="text-center mb-8">
              <p className="text-white/60 text-sm mb-2">Now playing for</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white capitalize">
                {selectedName}
              </h2>
            </div>
            
            {/* Waveform / Visual */}
            <div className="relative h-24 bg-white/5 rounded-lg mb-6 overflow-hidden">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Progress bar */}
                  <div 
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30 transition-all duration-100"
                    style={{ width: `${progress * 100}%` }}
                  />
                  
                  {/* Waveform bars (decorative) */}
                  <div className="absolute inset-0 flex items-center justify-center gap-1">
                    {Array.from({ length: 40 }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-1 rounded-full transition-all duration-150 ${
                          isPlaying ? 'bg-purple-400' : 'bg-white/20'
                        }`}
                        style={{
                          height: `${20 + Math.sin(i * 0.5) * 30 + (isPlaying ? Math.random() * 20 : 0)}%`,
                          opacity: i / 40 < progress ? 1 : 0.3
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Time display */}
            <div className="flex justify-between text-sm text-white/60 mb-4">
              <span>{formatTime(progress * duration)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {/* Restart */}
              <Button
                variant="ghost"
                size="icon"
                onClick={restartPlayback}
                disabled={!audioReady}
                className="text-white/60 hover:text-white"
                data-testid="restart-btn"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
              
              {/* Play/Pause */}
              <Button
                size="lg"
                onClick={togglePlay}
                disabled={!audioReady || isLoading}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="play-pause-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-8 h-8" />
                ) : (
                  <Play className="w-8 h-8 ml-1" />
                )}
              </Button>
              
              {/* Mute */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className="text-white/60 hover:text-white"
                data-testid="mute-btn"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </Button>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                onClick={resetSelection}
                className="bg-white/5 border-white/10 hover:bg-white/10"
                data-testid="change-name-btn"
              >
                <Music className="w-4 h-4 mr-2" />
                Start Over
              </Button>
              
              <Button
                variant="outline"
                onClick={downloadAudio}
                disabled={!audioReady}
                className="bg-white/5 border-white/10 hover:bg-white/10"
                data-testid="download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                MP3
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowVideoModal(true)}
                disabled={!audioReady || !user}
                className="bg-white/5 border-white/10 hover:bg-white/10"
                data-testid="create-video-btn"
                title={!user ? "Log in to create videos" : "Create shareable video"}
              >
                <Video className="w-4 h-4 mr-2" />
                Video
              </Button>
              
              {generatedVideoUrl && (
                <Button
                  onClick={() => setShowShare(true)}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              )}
            </div>
            
            {/* Video Generation Modal */}
            {showVideoModal && (
              <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-900 border border-white/10 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">
                      Create Birthday Video
                    </h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setShowVideoModal(false);
                        setGeneratedVideoUrl(null);
                        setGeneratedVideoId(null);
                      }}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <p className="text-white/60 mb-6">
                    Create an MP4 video for <span className="text-white font-semibold capitalize">{selectedName}</span> to share on Facebook, TikTok, and more!
                  </p>
                  
                  {!generatedVideoUrl ? (
                    <>
                      {/* Background Style */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-white mb-2">Background</label>
                        
                        {/* Preset Backgrounds */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                          {availableBackgrounds.map((bg) => (
                            <div key={bg.id} className="relative group">
                              <button
                                onClick={() => {
                                  setVideoStyle(bg.id);
                                  setCustomImageUrl('');
                                  setCustomImageFile(null);
                                  setCustomVideoUrl('');
                                  setCustomVideoFile(null);
                                }}
                                className={`w-full p-3 rounded-lg border-2 transition-all relative overflow-hidden ${
                                  videoStyle === bg.id 
                                    ? 'border-purple-500 bg-purple-500/20' 
                                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                                }`}
                              >
                                {bg.type === 'video' && (
                                  <span className="absolute top-1 right-1 bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                                    VIDEO
                                  </span>
                                )}
                                <Sparkles className="w-6 h-6 mx-auto mb-1 text-purple-400" />
                                <span className="text-white text-xs block">{bg.name}</span>
                              </button>
                              {/* Thumbnail preview on hover */}
                              {bg.thumbnail && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                  <div className="bg-zinc-900 rounded-lg p-1 shadow-xl border border-white/20">
                                    <img 
                                      src={bg.thumbnail} 
                                      alt={bg.name}
                                      className="w-24 h-24 rounded object-cover"
                                    />
                                    <p className="text-white text-xs text-center mt-1">{bg.name}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          
                          {/* Custom Image Option */}
                          <button
                            onClick={() => setVideoStyle('custom_image')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              videoStyle === 'custom_image' 
                                ? 'border-purple-500 bg-purple-500/20' 
                                : 'border-white/10 bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <ImagePlus className="w-6 h-6 mx-auto mb-1 text-pink-400" />
                            <span className="text-white text-xs block">Custom Photo</span>
                          </button>
                          
                          {/* Custom Video Option */}
                          <button
                            onClick={() => setVideoStyle('custom_video')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              videoStyle === 'custom_video' 
                                ? 'border-purple-500 bg-purple-500/20' 
                                : 'border-white/10 bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <Video className="w-6 h-6 mx-auto mb-1 text-pink-400" />
                            <span className="text-white text-xs block">Custom Video</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Custom Image Upload */}
                      {videoStyle === 'custom_image' && (
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-white mb-2">Upload Photo</label>
                          {customImageUrl ? (
                            <div className="relative">
                              <img 
                                src={customImageUrl} 
                                alt="Preview" 
                                className="w-full h-40 object-cover rounded-lg"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setCustomImageUrl('');
                                  setCustomImageFile(null);
                                }}
                                className="absolute top-2 right-2 bg-black/50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors">
                              <Upload className="w-8 h-8 text-white/40 mb-2" />
                              <span className="text-white/40 text-sm">Click to upload image</span>
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleImageUpload}
                                className="hidden" 
                              />
                            </label>
                          )}
                        </div>
                      )}
                      
                      {/* Custom Video Upload */}
                      {videoStyle === 'custom_video' && (
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-white mb-2">Upload Video (max 50MB)</label>
                          {customVideoUrl ? (
                            <div className="relative">
                              <video 
                                src={customVideoUrl} 
                                className="w-full h-40 object-cover rounded-lg"
                                muted
                                autoPlay
                                loop
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setCustomVideoUrl('');
                                  setCustomVideoFile(null);
                                }}
                                className="absolute top-2 right-2 bg-black/50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors">
                              <Upload className="w-8 h-8 text-white/40 mb-2" />
                              <span className="text-white/40 text-sm">Click to upload video</span>
                              <input 
                                type="file" 
                                accept="video/*" 
                                onChange={handleVideoUpload}
                                className="hidden" 
                              />
                            </label>
                          )}
                        </div>
                      )}
                      
                      {/* Audio Info - Show what audio will be used */}
                      <div className="mb-6 p-3 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-sm text-white/60 mb-1">Audio</p>
                        {audioSource === 'custom' && selectedUserSong ? (
                          <div className="flex items-center gap-2">
                            <Music className="w-4 h-4 text-purple-400" />
                            <span className="text-white">{selectedUserSong.title}</span>
                            <span className="text-amber-400 text-xs ml-auto">5 credits</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Cake className="w-4 h-4 text-pink-400" />
                            <span className="text-white">
                              Happy Birthday {isPersonalizedName ? `for ${selectedName}` : '(Classic)'}
                            </span>
                            <span className="text-green-400 text-xs ml-auto">FREE</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Text Position */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-white mb-2">Text Position</label>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { value: 'none', label: 'No Text' },
                            { value: 'top', label: 'Top' },
                            { value: 'center', label: 'Center' },
                            { value: 'bottom', label: 'Bottom' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => setTextPosition(opt.value)}
                              className={`p-2 rounded-lg border transition-all ${
                                textPosition === opt.value
                                  ? 'border-purple-500 bg-purple-500/20'
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              <span className="text-white text-xs">{opt.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Overlay Effect (confetti, sparkles) */}
                      {availableOverlays.length > 1 && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-white mb-2">Overlay Effect</label>
                          <div className="grid grid-cols-3 gap-2">
                            {availableOverlays.map((overlay) => (
                              <div key={overlay.id} className="relative group">
                                <button
                                  onClick={() => setVideoOverlay(overlay.id)}
                                  className={`w-full p-2 rounded-lg border transition-all ${
                                    videoOverlay === overlay.id
                                      ? 'border-purple-500 bg-purple-500/20'
                                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                                  }`}
                                >
                                  <span className="text-white text-xs">{overlay.name}</span>
                                </button>
                                {/* Thumbnail preview on hover */}
                                {overlay.thumbnail && (
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                    <div className="bg-zinc-900 rounded-lg p-1 shadow-xl border border-white/20">
                                      <img 
                                        src={overlay.thumbnail} 
                                        alt={overlay.name}
                                        className="w-24 h-24 rounded object-cover"
                                      />
                                      <p className="text-white text-xs text-center mt-1">{overlay.name}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Orientation */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-white mb-2">Video Size</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: 'square', label: 'Square', desc: '1:1' },
                            { value: 'portrait', label: 'Portrait', desc: '9:16' },
                            { value: 'landscape', label: 'Landscape', desc: '16:9' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => setVideoOrientation(opt.value)}
                              className={`p-3 rounded-lg border transition-all ${
                                videoOrientation === opt.value
                                  ? 'border-purple-500 bg-purple-500/20'
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              <span className="text-white text-sm block">{opt.label}</span>
                              <span className="text-white/40 text-xs">{opt.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Generate Button */}
                      <Button
                        onClick={generateVideo}
                        disabled={isGeneratingVideo || 
                          (videoStyle === 'custom_image' && !customImageUrl) ||
                          (videoStyle === 'custom_video' && !customVideoUrl) ||
                          (audioSource === 'custom' && !selectedUserSong)}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      >
                        {isGeneratingVideo ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating Video...
                          </>
                        ) : (
                          <>
                            <Video className="w-4 h-4 mr-2" />
                            Create Video {audioSource === 'custom' && '(5 credits)'}
                          </>
                        )}
                      </Button>
                      
                      {audioSource === 'custom' && (
                        <p className="text-center text-white/60 text-xs mt-2">
                          Using your own song costs 5 credits. Happy Birthday songs are free!
                        </p>
                      )}
                      
                      {isGeneratingVideo && (
                        <p className="text-center text-white/40 text-sm mt-3">
                          This may take up to a minute...
                        </p>
                      )}
                    </>
                  ) : (
                    /* Video Ready */
                    <div className="text-center">
                      <div className="mb-4 relative group">
                        <video 
                          id="birthday-video-player"
                          src={generatedVideoUrl} 
                          controls 
                          playsInline
                          webkit-playsinline="true"
                          className="w-full rounded-lg max-h-64"
                          autoPlay
                        />
                        {/* Manual fullscreen button for browsers that hide it */}
                        <button
                          onClick={() => {
                            const video = document.getElementById('birthday-video-player');
                            if (video) {
                              if (video.requestFullscreen) {
                                video.requestFullscreen();
                              } else if (video.webkitRequestFullscreen) {
                                video.webkitRequestFullscreen();
                              } else if (video.msRequestFullscreen) {
                                video.msRequestFullscreen();
                              }
                            }
                          }}
                          className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Fullscreen"
                        >
                          <Maximize className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={downloadVideo}
                          className="bg-gradient-to-r from-purple-500 to-pink-500"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download MP4
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setGeneratedVideoUrl(null);
                            setGeneratedVideoId(null);
                            setVideoStyle('balloons');
                            setCustomImageUrl('');
                            setCustomImageFile(null);
                          }}
                          className="bg-white/5 border-white/10"
                        >
                          Create Another
                        </Button>
                      </div>
                      
                      <p className="text-white/40 text-sm mt-4">
                        Download and share directly to Facebook, TikTok, Instagram!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Share Modal */}
            {showShare && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="glass-card rounded-xl p-6 max-w-md w-full">
                  <h3 className="text-xl font-bold text-white mb-4 text-center">
                    Share Birthday Song for {selectedName}
                  </h3>
                  
                  <div className="flex justify-center mb-6">
                    <ShareButtons 
                      type="birthday"
                      id={generatedVideoId}
                      title={selectedName}
                      videoUrl={generatedVideoUrl}
                    />
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => setShowShare(false)}
                    className="w-full bg-white/5 border-white/10"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Info Section */}
        <div className="mt-8 text-center text-white/40 text-sm">
          <p>
            Don't see the name you're looking for?{' '}
            <button 
              onClick={() => navigate('/feedback')}
              className="text-purple-400 hover:underline"
            >
              Request it here
            </button>
            {' '}and we'll add it!
          </p>
        </div>
      </main>
    </div>
  );
}
